// src/components/RequirementsModal.js
import React, { useState, useEffect, useRef } from 'react';
import { getRequirementsByProjectId, createRequirement, deleteRequirement, getProjectBudgetStatus } from '../api/requirementApi';
import RequirementsList from '../components/programDirector/RequirementList';
import DeleteConfirmationModal from '../components/programDirector/DeleteConfirmationModal';
import { FaBriefcase, FaTimes, FaDollarSign, FaCalendar, FaTag, FaBox, FaCalculator, FaFileContract } from 'react-icons/fa';

const COMMON_UNITS = [
  "hour(s)", "day(s)", "week(s)", "month(s)", "person(s)", "team(s)", "piece(s)", "unit(s)", "item(s)", "set(s)", "package(s)", "lot(s)", "kg(s)", "g(s)", "ton(s)", "liter(s)", "ml(s)", "m³(s)", "meter(s)", "cm(s)", "km(s)", "ft(s)", "yd(s)", "m²(s)", "event(s)", "trip(s)"
];


const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Section = ({ title, icon, children, defaultOpen = true }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="border border-gray-200 rounded-xl mb-4 overflow-hidden bg-white">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-left px-5 py-3 bg-gray-50 font-semibold text-gray-700 flex justify-between items-center hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-center space-x-2">
                    <span className="text-orange-500">{icon}</span>
                    <span>{title}</span>
                </div>
                <span className="text-gray-500 text-lg">{isOpen ? '▼' : '▲'}</span>
            </button>
            {isOpen && <div className="p-5">{children}</div>}
        </div>
    );
};

const FormField = ({ label, name, value, onChange, type = "text", placeholder = "", className = "", options = null, icon, ...props }) => (
    <div className={className}>
        <label htmlFor={name} className="block mb-1 font-medium text-gray-700">
            {label} {props.required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
            {icon && (
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {icon}
                </div>
            )}
            {type === 'select' ? (
                <select id={name} name={name} value={value} onChange={onChange} className={`w-full border border-gray-300 rounded-md px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 ${icon ? 'pl-10' : ''}`}>
                    {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
            ) : type === 'textarea' ? (
                <textarea id={name} name={name} value={value} onChange={onChange} placeholder={placeholder} rows="3" className={`w-full border border-gray-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 ${icon ? 'pl-10' : ''} `} {...props} />
            ) : (
                <input id={name} name={name} type={type} value={value} onChange={onChange} placeholder={placeholder} className={`w-full border border-gray-300 rounded-md px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500 ${icon ? 'pl-10' : ''} `} {...props} />
            )}
        </div>
    </div>
);

const RequirementsModal = ({ projectId, projectName, projectBudgetCode, onClose }) => {
  const [requirements, setRequirements] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  
  // CHANGE: Added state for real budget data
  const [budgetData, setBudgetData] = useState({
      targetBudget: 0,
      totalFundsReceived: 0,
      currency: "USD"
    });
  // CHANGE: Added loading state for budget
  const [loadingBudget, setLoadingBudget] = useState(true);

  const [newReq, setNewReq] = useState({
    name: '',
    type: '', // default is empty string so placeholder shows
    quantity: 1,
    unit: '',
    unitCost: 0,
    budgetCode: projectBudgetCode || '',
    neededBy: '',
    specification: ''
  });
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [requirementToDelete, setRequirementToDelete] = useState(null);

  const errorRef = useRef(null);
  const successRef = useRef(null);
  const formRef = useRef(null);
  const modalContentRef = useRef(null);

  const today = getTodayDate();
  // ✨ CHANGE: Removed DUMMY_BUDGET constant

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (success && successRef.current) {
      successRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error, success]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // ✨ CHANGE: New Effect to fetch real budget data
  useEffect(() => {
    const fetchBudget = async () => {
        setLoadingBudget(true);
        try {
            const data = await getProjectBudgetStatus(projectId);
            setBudgetData(data);
        } catch (err) {
            console.error("Failed to load budget data", err);
            // Keep defaults (0) on error
        } finally {
            setLoadingBudget(false);
        }
    };

    if (projectId) {
        fetchBudget();
    }
  }, [projectId]);

  useEffect(() => {
    const fetchRequirements = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchedRequirements = await getRequirementsByProjectId(projectId);
        setRequirements(fetchedRequirements);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setRequirements([]);
        } else {
          setError("Failed to fetch requirements. Please check your connection and try again.");
          console.error("Error fetching requirements:", err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchRequirements();
  }, [projectId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    setNewReq(prev => ({ ...prev, budgetCode: projectBudgetCode || '' }));
  }, [projectBudgetCode]);

  // ✨ CHANGE: Removed manual calculation of totalSpent and remainingBudget
  // These are now fetched from the backend via budgetData
  
  const projectTitle = projectName;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewReq(prev => {
      const updatedReq = { ...prev, [name]: value };
      if (name === 'quantity' || name === 'unitCost') {
        const quantity = parseFloat(updatedReq.quantity) || 0;
        const unitCost = parseFloat(updatedReq.unitCost) || 0;
        updatedReq.totalCost = quantity * unitCost;
      }
      return updatedReq;
    });
  };

  const handleAddClick = () => {
    setIsAdding(true);
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleAddRequirement = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    const { name, type, quantity, unit, unitCost, budgetCode, neededBy, specification } = newReq;
    
    // Validation with specific error messages
    if (!name || !name.trim()) {
      setError("Requirement name is required. Please provide a descriptive name.");
      setIsSaving(false);
      return;
    }
    
    if (!type) {
      setError("Please select a requirement type.");
      setIsSaving(false);
      return;
    }
    
    if (!quantity || quantity < 1) {
      setError("Quantity must be at least 1.");
      setIsSaving(false);
      return;
    }
    
    if (!unit || !unit.trim()) {
      setError("Unit is required. Please specify the unit of measurement.");
      setIsSaving(false);
      return;
    }
    
    if (!unitCost || unitCost < 0) {
      setError("Unit cost must be a positive number.");
      setIsSaving(false);
      return;
    }
    
    if (!budgetCode) {
      setError("Budget code is required for financial tracking.");
      setIsSaving(false);
      return;
    }
    
    if (!neededBy) {
      setError("Please select a needed by date for this requirement.");
      setIsSaving(false);
      return;
    }

    try {
      const requirementToSave = {
        name: name.trim(),
        type,
        quantity: parseFloat(quantity),
        unit: unit.trim(),
        unitCost: parseFloat(unitCost),
        budgetCode,
        projectId,
        neededBy,
        specification: specification.trim(),
        status: 'Pending',
      };

      const addedReq = await createRequirement(requirementToSave);
      setRequirements(prev => [...prev, addedReq]);
      setNewReq({
        name: '',
        type: '',
        quantity: 1,
        unit: '',
        unitCost: 0,
        budgetCode: '',
        neededBy: '',
        specification: ''
      });
      setIsAdding(false);
      setSuccess("Requirement created successfully!");
      setError(null);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setError(`Failed to add requirement: ${err.response.data.message}`);
      } else {
        setError("Failed to add requirement. Please check your connection and try again.");
      }
      console.error("Error adding requirement:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDeleteModal = (requirement) => {
    setRequirementToDelete(requirement);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleteModalOpen(false);
    if (!requirementToDelete) return;
    
    setLoading(true);
    try {
      await deleteRequirement(requirementToDelete._id);
      setRequirements(requirements.filter(req => req._id !== requirementToDelete._id));
      setSuccess("Requirement deleted successfully!");
      setError(null);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setError(`Failed to delete requirement: ${err.response.data.message}`);
      } else {
        setError("Failed to delete requirement. Please check your connection and try again.");
      }
      console.error("Error deleting requirement:", err);
    } finally {
      setLoading(false);
      setRequirementToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setRequirementToDelete(null);
  };

  // Removed invalid isOpen check

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
      <div ref={modalContentRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header with Orange Gradient */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-50">
          <div className="flex items-center justify-between px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                <FaBriefcase className="w-7 h-7 text-black" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-black">Requirements for: <span className="text-orange-600">{projectTitle}</span></h2>
                <p className="text-black-100 mt-1">Project Management System</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-grow px-8 py-6 overflow-y-auto">
          {success && (
            <div ref={successRef} className="bg-green-100 text-green-700 p-4 rounded-md mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {success}
            </div>
          )}

          {error && (
            <div ref={errorRef} className="bg-red-100 text-red-700 p-4 rounded-md mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          {/* ✨ CHANGE: Updated Budget Overview Section to use real data */}
          {/* ✏️ UPDATED SECTION: Budget Overview (Simplified) */}
          <Section title="Budget Overview" icon={<FaDollarSign />}>
            {loadingBudget ? (
                <div className="flex justify-center py-4 text-gray-500">
                    Loading budget details...
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Total Budget (Target) */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="font-medium text-blue-800">Project Budget (Target)</p>
                        <p className="text-2xl font-bold text-blue-600">${budgetData.targetBudget.toLocaleString()}</p>
                        <p className="text-xs text-blue-400 mt-1">Target allocation for {projectBudgetCode}</p>
                    </div>
                    
                    {/* Total Funds Received */}
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <p className="font-medium text-green-800">Total Funds Received</p>
                        <p className="text-2xl font-bold text-green-600">${budgetData.totalFundsReceived.toLocaleString()}</p>
                        <p className="text-xs text-green-400 mt-1">Based on donations for {projectBudgetCode}</p>
                    </div>
                </div>
            )}
          </Section>


          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-semibold text-gray-700">Requirements</h3>
              <button
                onClick={handleAddClick}
                className="bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white font-bold py-2 px-4 rounded-full transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50"
              >
                + Add Requirement
              </button>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                <svg className="animate-spin h-8 w-8 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="mt-3">Loading requirements...</p>
              </div>
            ) : (
              <RequirementsList 
                requirements={requirements} 
                onOpenDeleteModal={handleOpenDeleteModal} 
                projectId={projectId}
              />
            )}
          </div>

          {isAdding && (
            <div ref={formRef} className="bg-orange-50 p-6 rounded-lg border-2 border-orange-300">
              <h3 className="text-2xl font-semibold text-orange-700 mb-4">New Requirement</h3>
              <form onSubmit={handleAddRequirement} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <FormField 
                  label="Name" 
                  name="name" 
                  value={newReq.name} 
                  onChange={handleInputChange} 
                  required 
                  icon={<FaTag className="h-4 w-4 text-gray-400" />}
                />
                <FormField 
                  label="Type" 
                  name="type" 
                  type="select" 
                  value={newReq.type} 
                  onChange={handleInputChange} 
                  options={[
                    { value: '', label: 'Select Type', disabled: true },
                    { value: 'STAFF', label: 'STAFF' },
                    { value: 'ITEM', label: 'ITEM' },
                    { value: 'SERVICE', label: 'SERVICE' }
                  ]}
                  required 
                  icon={<FaBox className="h-4 w-4 text-gray-400" />}
                />
                <FormField 
                  label="Budget Code" 
                  name="budgetCode" 
                  type="text" 
                  value={newReq.budgetCode} 
                  onChange={handleInputChange} 
                  readOnly
                  className="bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300"
                  required 
                  icon={<FaDollarSign className="h-4 w-4 text-gray-400" />}
                  placeholder="Auto-assigned from Project"
                />
                <FormField 
                  label="Quantity" 
                  name="quantity" 
                  type="number" 
                  value={newReq.quantity} 
                  onChange={handleInputChange} 
                  min="1" 
                  required 
                  icon={<FaCalculator className="h-4 w-4 text-gray-400" />}
                />
                <FormField 
                  label="Unit" 
                  name="unit" 
                  value={newReq.unit} 
                  onChange={handleInputChange} 
                  placeholder="e.g., piece, hour"
                  required 
                  icon={<FaBox className="h-4 w-4 text-gray-400" />}
                />
                <FormField 
                  label="Unit Cost" 
                  name="unitCost" 
                  type="number" 
                  value={newReq.unitCost} 
                  onChange={handleInputChange} 
                  min="0" 
                  step="0.01" 
                  required 
                  icon={<FaDollarSign className="h-4 w-4 text-gray-400" />}
                />
                <FormField 
                  label="Needed By" 
                  name="neededBy" 
                  type="date" 
                  value={newReq.neededBy} 
                  onChange={handleInputChange} 
                  min={today} 
                  required 
                  icon={<FaCalendar className="h-4 w-4 text-gray-400" />}
                />
                <div className="md:col-span-2 lg:col-span-3">
                  <FormField 
                    label="Specification (Optional)" 
                    name="specification" 
                    type="textarea" 
                    value={newReq.specification} 
                    onChange={handleInputChange} 
                    placeholder="e.g., Must be stainless steel, 304 grade OR Requires 5 years of Python experience"
                    icon={<FaFileContract className="h-4 w-4 text-gray-400" />}
                  />
                  <p className="text-gray-500 text-xs mt-1">Provide detailed specifications or requirements for this item/service/staff.</p>
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-gray-700 font-medium mb-1">Total Cost (Auto-calculated)</label>
                  <p className="text-3xl font-bold text-orange-600">${newReq.totalCost ? newReq.totalCost.toLocaleString() : '0'}</p>
                </div>
                <div className="md:col-span-2 lg:col-span-3 flex justify-end gap-4 mt-4">
                  <button
                    type="button"
                    onClick={() => setIsAdding(false)}
                    className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className={`px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 ${
                      isSaving 
                        ? 'bg-orange-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {isSaving ? (
                      <div className="flex items-center space-x-3">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <span>Save Requirement</span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        itemName={requirementToDelete?.name || ''}
      />
    </div>
  );
};

export default RequirementsModal;